/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from 'react';
import { Box, CircularProgress, IconButton, useTheme } from '@mui/material';
import useStyles from './FileContainer.styles.ts';
import { Typo } from 'components/primitive';
import FileIcon from '@mui/icons-material/Description';
import ClearIcon from '@mui/icons-material/Clear';
import ImageIcon from '@mui/icons-material/Image';
import { fileLinkURIs } from 'utils/FileUploadAPI.ts';
import FileThumbnail from './FileThumbnail.tsx';
import { useFileDownload } from './useFileDownload.ts';

type FileContainerProps = {
  name: string;
  bytes: string;
  authToken: string;
  readOnly?: boolean;
  item: any;
  /** Resolves to a link to offer inline when the browser blocked the new tab, else null. */
  downloadFile?: (item: any, preferredURI?: string) => Promise<string | null>;
  removeFile?: (item: any) => void;
};

function FileContainer({ readOnly = true, ...props }: FileContainerProps) {
  const theme = useTheme();
  const classes = useStyles(theme);
  // *: The path this file's thumbnail loaded from, tried first when it downloads.
  const [loadedURI, setLoadedURI] = useState<string>();
  const { downloadPending, blockedLink, handleDownload, handleDownloadKeyDown } = useFileDownload({
    downloadFile: props.downloadFile,
    item: props.item,
    loadedURI,
  });

  // *: Check if the file is an image
  const isImage =
    props.item && Object.prototype.hasOwnProperty.call(props.item, 'cbrnDataFileName')
      ? props.item.cbrnDataFileName.toLocaleLowerCase().match(/\.(jpeg|jpg|gif|png)$/) != null
      : props.item.file_name.toLocaleLowerCase().match(/\.(jpeg|jpg|gif|png)$/) != null;
  // *: The Files API paths the thumbnail resolves a short-lived link from
  const uris = useMemo(() => fileLinkURIs(props.item), [props.item]);

  const handleRemove = () => {
    if (props.removeFile) props.removeFile(props.item);
    return null;
  };

  return (
    <Box sx={classes.file}>
      <Box
        sx={{ ...classes.fileContent, ...(downloadPending ? classes.fileContentPending : {}) }}
        role={props.downloadFile ? 'button' : undefined}
        tabIndex={props.downloadFile ? 0 : undefined}
        aria-label={props.downloadFile ? `Download ${props.name}` : undefined}
        aria-busy={downloadPending || undefined}
        aria-disabled={downloadPending || undefined}
        onClick={props.downloadFile ? handleDownload : undefined}
        onKeyDown={props.downloadFile ? handleDownloadKeyDown : undefined}>
        <Box sx={classes.rowContainer}>
          {downloadPending ? (
            <CircularProgress size={20} />
          ) : isImage ? (
            // If the file is an image, create a thumbnail
            <ImageIcon sx={{ width: '24px', color: theme.palette.grey[500] }} />
          ) : (
            // If the file is not an image, display the file icon
            <FileIcon sx={{ width: '24px', color: theme.palette.grey[500] }} />
          )}
          <Box sx={classes.fileNameWrapper}>
            <Typo sx={classes.fileName}>{props.name}</Typo>
            <Typo sx={classes.fileSize}>{props.bytes}</Typo>
          </Box>
          <Box sx={classes.removeBtn} hidden={readOnly}>
            <IconButton
              aria-label='Delete'
              aria-haspopup='true'
              onClick={props.removeFile ? handleRemove : undefined}>
              <ClearIcon className='icon-small' />
            </IconButton>
          </Box>
        </Box>
        {isImage && (
          <FileThumbnail
            key={uris.join('|')}
            uris={uris}
            authToken={props.authToken}
            onLoad={setLoadedURI}
          />
        )}
      </Box>
      {/* Outside the download button, so opening the link cannot start a second
          download. Mounted from the start so screen readers announce the link. */}
      <Box aria-live='polite'>
        {blockedLink ? (
          // *: Re-resolves on click rather than navigating the stored link: a
          // *: presigned URL can expire between the block and this click, and a
          // *: fresh resolve is the same handleDownload flow the row already uses.
          <Box
            component='a'
            role='button'
            tabIndex={0}
            sx={classes.blockedLink}
            onClick={(event) => {
              event.preventDefault();
              handleDownload();
            }}
            onKeyDown={handleDownloadKeyDown}>
            Download ready -- click to open
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export default FileContainer;
